import React, { useState, useEffect } from 'react';
import { editJob, unarchiveJob, fetchChoices, createJobInvoice, getJobPhotos, uploadJobPhotos, deleteJobPhoto } from '../api'; // API method to update a job and unarchiveJob
import './joborder.css'; // CSS for JobOrder component
import TireDisposalToggle from '../TireDisposalToggle';
import JobHistory from './jobhistory'; // Component to display job history
import WorkOrder from '../workorder';
import { useNavigate } from 'react-router-dom';
import JobSubscriptionButton from '../JobSubscriptionButton';
import InvoiceModal from '../InvoiceModal';

const STATUS_OPTIONS = [
    "tires",
    "tank",
    "prep",
    "paint",
    "lathe",
    "ovens",
    "mount&balance",
    "ready"
];

const JobOrder = ({ job, onClose, fromWorkOrder, onArchiveJob, onCopyJob }) => {
  const [editedJob, setEditedJob] = useState({ ...job });
  const [choices, setChoices] = useState({});
  const [showHistory, setShowHistory] = useState(false);
  const [showWorkOrder, setShowWorkOrder] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [showArchiveConfirmation, setShowArchiveConfirmation] = useState(false);
  const [showUnarchiveConfirmation, setShowUnarchiveConfirmation] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [showMobileMoreDropdown, setShowMobileMoreDropdown] = useState(false);
  const [showFullscreenImage, setShowFullscreenImage] = useState(false);
  const [showFullscreenControls, setShowFullscreenControls] = useState(true);
  const [imageScale, setImageScale] = useState(1);
  const [imagePosition, setImagePosition] = useState({ x: 0, y: 0 });
  const [additionalPhotos, setAdditionalPhotos] = useState([]);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const navigate = useNavigate();
  const fileInputRef = React.useRef(null);
  const additionalPhotosInputRef = React.useRef(null);
  const lastTouchDistance = React.useRef(null);
  const lastTouchCenter = React.useRef(null);
  const isPinching = React.useRef(false);

  // Ensure photo URL is set when component mounts or job changes
  useEffect(() => {
    if (job) {
      setEditedJob(prev => ({
        ...prev,
        picture: job.picture
      }));
    }
  }, [job]);

  // Reset showWorkOrder when job changes (prevents invoice modal from reappearing)
  useEffect(() => {
    setShowWorkOrder(false);
  }, [job]);

  // Load choices from API
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const choicesData = await fetchChoices();
        setChoices(choicesData);
      } catch (error) {
        console.error('Error loading choices:', error);
      }
    };
    loadChoices();
  }, []);

  // Load additional photos for the job
  useEffect(() => {
    const loadPhotos = async () => {
      if (job && job.id) {
        try {
          const photos = await getJobPhotos(job.id);
          setAdditionalPhotos(photos);
        } catch (error) {
          console.error('Error loading job photos:', error);
        }
      }
    };
    loadPhotos();
  }, [job]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Map frontend field names to backend field names
    const fieldMapping = {
      'shopNotes': 'shop_notes',
      'customerNotes': 'customer_notes',
      'timePromised': 'time_promised',
      'datePromised': 'date_promised'
    };
    
    const backendFieldName = fieldMapping[name] || name;
    setEditedJob(prev => ({ ...prev, [backendFieldName]: value }));
    setHasUnsavedChanges(true);
  };

  const handleStatusChange = (direction) => {
    if (!choices.status) return;
    
    const statusOptions = choices.status.sort((a, b) => a.order - b.order).map(choice => choice.value);
    const currentIndex = statusOptions.indexOf(editedJob.status);
    let newIndex;
    
    if (direction === 'next') {
      newIndex = currentIndex === statusOptions.length - 1 ? 0 : currentIndex + 1;
    } else {
      newIndex = currentIndex === 0 ? statusOptions.length - 1 : currentIndex - 1;
    }
    
    setEditedJob({ ...editedJob, status: statusOptions[newIndex] });
    setHasUnsavedChanges(true);
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => onClose(null)); // Pass null to indicate no changes should be applied
    } else {
      onClose(editedJob);
    }
  };

  const handleConfirmClose = () => {
    setShowUnsavedWarning(false);
    if (pendingAction) {
      pendingAction();
    }
  };

  const handleCancelClose = () => {
    setShowUnsavedWarning(false);
    setPendingAction(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission while the photo uploads
    if (isSaving) {
      return;
    }
    setIsSaving(true);

    const pictureFile = editedJob.picture instanceof File ? editedJob.picture : null;

    try {
      // Create FormData object for multipart/form-data
      const formData = new FormData();
      
      // Add all job data to FormData, including empty values
      Object.keys(editedJob).forEach(key => {
        if (key === 'picture') {
          if (editedJob[key] instanceof File) {
            formData.append('picture', editedJob[key]);
          }
          // Skip if it's a URL or null/undefined
        } else if (key === 'customer') {
          // Always send the integer PK; fall back to the original job prop if state is stale
          formData.append('customer', editedJob.customer || job.customer);
        } else {
          // Include all fields, even if they're empty
          formData.append(key, editedJob[key] || '');
        }
      });

      // Call the API to update the job
      const updatedJob = await editJob(editedJob.id, formData);

      // The job saved but the new photo didn't - keep the modal open so the user can retry
      if (pictureFile && (!updatedJob.picture || updatedJob.picture === job.picture)) {
        alert('The job was saved, but the photo did not upload. Please tap Save to try again.');
        return;
      }

      // Update the local state with the response
      setEditedJob(updatedJob);
      setHasUnsavedChanges(false);
      
      // Call the onClose callback with the updated job
      if (onClose) {
        onClose(updatedJob);
      }
    } catch (error) {
      console.error('Error updating job:', error);
      if (error.response?.data) {
        console.error('Server error details:', error.response.data);
        alert('Failed to update job: ' + JSON.stringify(error.response.data));
      } else {
        alert('Failed to update job: ' + error.message);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleHistoryClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowHistory(true));
    } else {
      setShowHistory(true);
    }
  };

  const handleWorkOrderClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowWorkOrder(true));
    } else {
      setShowWorkOrder(true);
    }
  };

  const handleCustomerClick = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => {
        localStorage.setItem('selectedCustomerId', job.customer);
        navigate('/accounts');
      });
    } else {
      localStorage.setItem('selectedCustomerId', job.customer);
      navigate('/accounts');
    }
  };

  const handleWorkOrderClose = (updatedWorkOrder) => {
    // Always reset showWorkOrder first to prevent any modal state issues
    setShowWorkOrder(false);
    
    if (updatedWorkOrder) {
      // Ensure the work order has the customer information from the job
      const workOrderWithCustomer = {
        ...updatedWorkOrder,
        customer: job.customer,
        customer_name: job.customer_name || job.customer
      };
      
      setEditedJob(prev => ({
        ...prev,
        work_order: workOrderWithCustomer
      }));
      
      // Pass the updated job back to the parent
      if (onClose) {
        onClose({
          ...editedJob,
          work_order: workOrderWithCustomer
        });
      }
    }
    // Don't close the job modal when canceling out of work order
  };

  const handleArchive = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowArchiveConfirmation(true));
    } else {
      setShowArchiveConfirmation(true);
    }
  };

  const handleConfirmArchive = () => {
    if (onArchiveJob && job) {
      onArchiveJob(job.id);
    }
    setShowArchiveConfirmation(false);
  };

  const handleCancelArchive = () => {
    setShowArchiveConfirmation(false);
  };

  const handleUnarchive = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowUnarchiveConfirmation(true));
    } else {
      setShowUnarchiveConfirmation(true);
    }
  };

  const handleConfirmUnarchive = async () => {
    if (onArchiveJob && job) {
      onArchiveJob(job.id);
    }
    setShowUnarchiveConfirmation(false);
  };

  const handleCancelUnarchive = () => {
    setShowUnarchiveConfirmation(false);
  };

  const handleMobileMoreClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowMobileMoreDropdown(!showMobileMoreDropdown);
  };

  const handleMobileHistoryClick = () => {
    setShowMobileMoreDropdown(false);
    handleHistoryClick();
  };

  const handleMobileWorkOrderClick = () => {
    setShowMobileMoreDropdown(false);
    handleWorkOrderClick();
  };

  const handleCopy = () => {
    if (onCopyJob) {
      onCopyJob(job);
    }
    onClose();
  };

  const handleMobileCopy = () => {
    setShowMobileMoreDropdown(false);
    handleCopy();
  };

  const handleMobileArchive = () => {
    setShowMobileMoreDropdown(false);
    handleArchive();
  };

  const handleMobileUnarchive = () => {
    setShowMobileMoreDropdown(false);
    handleUnarchive();
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showMobileMoreDropdown && 
          !event.target.closest('.mobile-more-button') && 
          !event.target.closest('.mobile-more-dropdown')) {
        setShowMobileMoreDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showMobileMoreDropdown]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Show the original right away, then swap in the compressed version when ready
    setEditedJob(prev => ({
      ...prev,
      picture: file
    }));
    setHasUnsavedChanges(true);

    const compressed = await compressIfNeeded(file);
    if (compressed !== file) {
      setEditedJob(prev => (prev.picture === file ? { ...prev, picture: compressed } : prev));
    }
  };

  const handleImageClick = (e) => {
    // If clicking on the photo itself, open fullscreen with gallery
    if (editedJob.picture && e.target.classList.contains('job-photo')) {
      e.stopPropagation();
      setCurrentGalleryIndex(0); // Start with main photo
      setShowFullscreenImage(true);
      setShowFullscreenControls(true);
    }
  };

  const handlePhotoContainerClick = () => {
    // Click on container opens file picker if no photo
    if (!editedJob.picture) {
      fileInputRef.current.click();
    }
  };

  // Compress image before upload - optimized for memory (especially Android)
  // Returns original file if compression would cause issues
  const compressImage = (file, maxWidth = 1920, quality = 0.8) => {
    return new Promise((resolve) => {
      // Always resolve, never reject - prevents unhandled promise rejections
      try {
        const reader = new FileReader();
        
        reader.onload = (e) => {
          try {
            const img = new Image();
            let resolved = false; // Track if we've already resolved
            
            img.onload = () => {
              if (resolved) return; // Prevent multiple resolutions
              
              try {
                const canvas = document.createElement('canvas');
                let width = img.width;
                let height = img.height;
                
                // Calculate new dimensions
                if (width > maxWidth) {
                  height = (height / width) * maxWidth;
                  width = maxWidth;
                }
                
                // Limit canvas size to prevent memory issues
                const maxCanvasSize = 4096; // Max canvas dimension
                if (width > maxCanvasSize || height > maxCanvasSize) {
                  const scale = Math.min(maxCanvasSize / width, maxCanvasSize / height);
                  width = width * scale;
                  height = height * scale;
                }
                
                canvas.width = width;
                canvas.height = height;
                
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                
                canvas.toBlob((blob) => {
                  if (resolved) return; // Prevent multiple resolutions
                  
                  // Clean up memory immediately (remove error handler first to prevent false errors)
                  try {
                    img.onerror = null; // Remove error handler before cleanup
                    img.onload = null; // Remove load handler
                    img.src = '';
                    canvas.width = 0;
                    canvas.height = 0;
                  } catch (cleanupError) {
                    // Ignore cleanup errors
                  }
                  
                  if (!blob) {
                    console.warn('Canvas toBlob returned null, using original file');
                    resolved = true;
                    resolve(file);
                    return;
                  }
                  
                  const compressedFile = new File([blob], file.name, {
                    type: 'image/jpeg',
                    lastModified: Date.now()
                  });
                  resolved = true;
                  resolve(compressedFile);
                }, 'image/jpeg', quality);
              } catch (error) {
                if (resolved) return;
                console.warn('Canvas operation failed, using original:', error);
                resolved = true;
                resolve(file);
              }
            };
            
            img.onerror = () => {
              if (resolved) return; // Ignore errors after resolution
              console.warn('Image load failed, using original');
              resolved = true;
              resolve(file);
            };
            
            img.src = e.target.result;
          } catch (error) {
            console.warn('Image processing error, using original:', error);
            resolve(file);
          }
        };
        
        reader.onerror = () => {
          console.warn('FileReader error, using original file');
          resolve(file);
        };
        
        reader.readAsDataURL(file);
      } catch (error) {
        // If compression fails entirely, use original file
        console.warn('Compression error, using original file:', error);
        resolve(file);
      }
    });
  };

  // Compress an image based on its size; returns the original for small or non-image files
  const compressIfNeeded = async (file) => {
    if (!file || !file.type || !file.type.startsWith('image/')) {
      return file;
    }
    const fileSizeMB = file.size / 1024 / 1024;

    // Skip compression for very small files (already optimized)
    if (fileSizeMB < 0.5) {
      console.log(`📸 Small image (${fileSizeMB.toFixed(2)} MB) - skipping compression`);
      return file;
    }

    let compressed = file;
    try {
      // For large files, use more aggressive compression
      if (fileSizeMB > 10) {
        console.warn(`Large image detected: ${fileSizeMB.toFixed(2)} MB - using smaller compression`);
        compressed = await compressImage(file, 1280, 0.7);
      } else if (fileSizeMB > 5) {
        // Medium-large files: moderate compression
        console.log(`📸 Compressing medium image: ${fileSizeMB.toFixed(2)} MB`);
        compressed = await compressImage(file, 1600, 0.75);
      } else {
        // Normal files: standard compression
        console.log(`📸 Compressing image: ${fileSizeMB.toFixed(2)} MB`);
        compressed = await compressImage(file);
      }
      console.log(`✅ Compressed to: ${(compressed.size / 1024 / 1024).toFixed(2)} MB`);
    } catch (error) {
      // Compression always resolves, but catch just in case
      console.warn('Compression error, using original file:', error);
      compressed = file;
    }
    return compressed;
  };

  const handleAdditionalPhotosUpload = async (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      setIsUploadingPhotos(true);
      try {
        // Compress images before upload to reduce memory usage
        const compressedFiles = [];
        for (const file of Array.from(files)) {
          compressedFiles.push(await compressIfNeeded(file));
        }

        const uploadedPhotos = await uploadJobPhotos(job.id, compressedFiles);
        setAdditionalPhotos(prev => [...prev, ...uploadedPhotos]);
        // Clear the input
        if (additionalPhotosInputRef.current) {
          additionalPhotosInputRef.current.value = '';
        }
      } catch (error) {
        console.error('Error uploading photos:', error);
        alert('Failed to upload photos. Please try again.');
      } finally {
        setIsUploadingPhotos(false);
      }
    }
  };

  const handleDeletePhoto = async (photoId, e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (window.confirm('Are you sure you want to delete this photo?')) {
      try {
        await deleteJobPhoto(photoId);
        setAdditionalPhotos(prev => prev.filter(p => p.id !== photoId));
        // If we're viewing a deleted photo in gallery, move to next/prev
        if (showFullscreenImage && currentGalleryIndex > 0) {
          const newIndex = Math.min(currentGalleryIndex, additionalPhotos.length);
          setCurrentGalleryIndex(newIndex);
        }
      } catch (error) {
        console.error('Error deleting photo:', error);
        alert('Failed to delete photo. Please try again.');
      }
    }
  };

  const handleGalleryNext = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const totalPhotos = 1 + additionalPhotos.length; // Main photo + additional
    setCurrentGalleryIndex((prev) => (prev + 1) % totalPhotos);
    // Reset zoom when changing photos
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const handleGalleryPrev = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const totalPhotos = 1 + additionalPhotos.length;
    setCurrentGalleryIndex((prev) => (prev - 1 + totalPhotos) % totalPhotos);
    // Reset zoom when changing photos
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
  };

  const getCurrentGalleryPhoto = () => {
    if (currentGalleryIndex === 0) {
      // Main photo
      return editedJob.picture instanceof File 
        ? URL.createObjectURL(editedJob.picture) 
        : editedJob.picture;
    } else {
      // Additional photo
      const photo = additionalPhotos[currentGalleryIndex - 1];
      return photo ? photo.image : null;
    }
  };

  const handleChangePhotoFromFullscreen = (e) => {
    e.stopPropagation();
    // Close fullscreen first, then open file picker
    setShowFullscreenImage(false);
    setShowFullscreenControls(true);
    setImageScale(1);
    setImagePosition({ x: 0, y: 0 });
    // Small delay to ensure fullscreen closes before opening file picker
    setTimeout(() => {
      fileInputRef.current.click();
    }, 100);
  };

  const handleCloseFullscreen = (e, forceClose = false) => {
    // If forceClose is true (from close button), always close
    if (forceClose) {
      setShowFullscreenImage(false);
      setShowFullscreenControls(true);
      setImageScale(1);
      setImagePosition({ x: 0, y: 0 });
      setCurrentGalleryIndex(0);
      return;
    }
    
    // Only close if clicking directly on the viewer background
    if (e && e.target === e.currentTarget) {
      setShowFullscreenImage(false);
      setShowFullscreenControls(true);
      setImageScale(1);
      setImagePosition({ x: 0, y: 0 });
      setCurrentGalleryIndex(0);
    }
  };

  const handleToggleFullscreenControls = () => {
    if (!isPinching.current) {
      setShowFullscreenControls(prev => !prev);
    }
  };

  const getTouchDistance = (touch1, touch2) => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getTouchCenter = (touch1, touch2) => {
    return {
      x: (touch1.clientX + touch2.clientX) / 2,
      y: (touch1.clientY + touch2.clientY) / 2
    };
  };

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      e.preventDefault();
      isPinching.current = true;
      lastTouchDistance.current = getTouchDistance(e.touches[0], e.touches[1]);
      lastTouchCenter.current = getTouchCenter(e.touches[0], e.touches[1]);
    }
  };

  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && isPinching.current) {
      e.preventDefault();
      
      const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
      const currentCenter = getTouchCenter(e.touches[0], e.touches[1]);
      
      if (lastTouchDistance.current) {
        const scaleChange = currentDistance / lastTouchDistance.current;
        const newScale = Math.max(0.5, Math.min(5, imageScale * scaleChange));
        
        // Calculate position adjustment to zoom toward pinch center
        if (lastTouchCenter.current) {
          const deltaX = currentCenter.x - lastTouchCenter.current.x;
          const deltaY = currentCenter.y - lastTouchCenter.current.y;
          
          setImagePosition(prev => ({
            x: prev.x + deltaX,
            y: prev.y + deltaY
          }));
        }
        
        setImageScale(newScale);
        lastTouchCenter.current = currentCenter;
      }
      
      lastTouchDistance.current = currentDistance;
    }
  };

  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) {
      isPinching.current = false;
      lastTouchDistance.current = null;
      lastTouchCenter.current = null;
      
      // Reset if zoomed out too far
      if (imageScale < 1) {
        setImageScale(1);
        setImagePosition({ x: 0, y: 0 });
      }
    }
  };

  const handleConvertToInvoice = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedWarning(true);
      setPendingAction(() => () => setShowInvoiceModal(true));
    } else {
      setShowInvoiceModal(true);
    }
  };

  const handleInvoiceModalClose = () => {
    setShowInvoiceModal(false);
  };

  const handleInvoiceConfirm = (invoiceData) => {
    console.log('Invoice created for job:', invoiceData);
    // Update the job with invoice information
    setEditedJob(prev => ({
      ...prev,
      invoice_number: invoiceData.invoice_number,
      invoice_link: invoiceData.invoice_link
    }));
    
    // Call onClose with updated job data
    if (onClose) {
      onClose({
        ...editedJob,
        invoice_number: invoiceData.invoice_number,
        invoice_link: invoiceData.invoice_link
      });
    }
  };

  if (showInvoiceModal) {
    // Convert job to work order format for the InvoiceModal
    // Use PO number as private note (without label)
    const privateNote = editedJob.po || '';
    
    // Build description with RO number if available
    let description = editedJob.make || 'Service';
    if (editedJob.ro) {
      description = `${description} - RO: ${editedJob.ro}`;
    }
    
    const jobAsWorkOrder = {
      ...editedJob,
      id: editedJob.id,
      customer: { name: '' }, // Keep billing address blank for jobs - use QuickBooks customer address
      notes: privateNote,
      price: 0, // Jobs don't have a price field
      number_of_wheels: editedJob.quantity || 1, // Use job's quantity field
      vehicle: description
    };

    return (
      <InvoiceModal
        workOrder={jobAsWorkOrder}
        onClose={handleInvoiceModalClose}
        onConfirm={handleInvoiceConfirm}
        isJobInvoice={true}
        jobId={editedJob.id}
        customerId={editedJob.customer || job.customer}
      />
    );
  }

  if (showWorkOrder && job.work_order) {
    return (
      <div>
        <WorkOrder 
          workOrder={{
            ...job.work_order,
            customer: job.customer,
            customer_name: job.customer_name || job.customer
          }}
          onClose={handleWorkOrderClose} 
          fromJobOrder={true}
        />
      </div>
    );
  }

  return (
    <form className='joborder' onSubmit={handleSubmit}>
      <div className="joborder-header-container">
        <div className="joborder-header-left">
          <h2 className='joborderheader'>Job Order #{job.id}</h2>
          <select
            name="contact"
            value={editedJob.contact || ''}
            onChange={handleChange}
            className='input-field'
          >
            <option value="">Select Contact</option>
            {choices.contact && choices.contact
              .sort((a, b) => a.order - b.order)
              .map(choice => (
                <option key={choice.value} value={choice.value}>
                  {choice.display_name}
                </option>
              ))}
          </select>
        </div>
        <div className="joborder-header-right">
          <JobSubscriptionButton job={job} />
          <button type="button" className="close-button" onClick={handleClose}>×</button>
        </div>
      </div>
      <div className='joborder-container'>
        <div className='joborder-left'>
          <div className='joborder-top'>
            <div className='joborder-photo' onClick={handlePhotoContainerClick}>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageChange}
                accept="image/*"
                style={{ display: 'none' }}
              />
              <input
                type="file"
                ref={additionalPhotosInputRef}
                onChange={handleAdditionalPhotosUpload}
                accept="image/*"
                multiple
                style={{ display: 'none' }}
              />
              {editedJob.picture ? (
                <img 
                  src={editedJob.picture instanceof File ? URL.createObjectURL(editedJob.picture) : editedJob.picture} 
                  alt="Job" 
                  className='job-photo'
                  onClick={handleImageClick}
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                  onLoad={(e) => {
                    e.target.style.display = 'block';
                  }}
                />
              ) : (
                <div className='job-photo-placeholder'>
                  <span>Click to add photo</span>
                </div>
              )}
            </div>
            {editedJob.picture && (
              <div className='additional-photos-section'>
                <button 
                  type="button" 
                  className='add-photos-button'
                  onClick={(e) => {
                    e.stopPropagation();
                    additionalPhotosInputRef.current.click();
                  }}
                  disabled={isUploadingPhotos}
                >
                  {isUploadingPhotos ? 'Uploading...' : `+ Add Photos ${additionalPhotos.length > 0 ? `(${additionalPhotos.length})` : ''}`}
                </button>
              </div>
            )}
            <div className='joborder-details'>
              <div className='joborder-basic-info'>
                <div className='info-row'>
                  <div className='info-column'>
                    <div className='info-label'>Quantity</div>
                    <input type="number" name="quantity" value={editedJob.quantity} onChange={handleChange} className='input-field' />
                  </div>
                  <div className='info-column'>
                    <div className='info-label'>Make</div>
                    <input type="text" name="make" value={editedJob.make} onChange={handleChange} className='input-field' />
                  </div>
                  <div className='info-column'>
                    <div className='info-label'>Service</div>
                    <select name="service" value={editedJob.service} onChange={handleChange} className='input-field'>
                      <option value="">Select Service</option>
                      {choices.service && choices.service
                        .sort((a, b) => a.order - b.order)
                        .map(choice => (
                          <option key={choice.value} value={choice.value}>
                            {choice.display_name}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>
                <div className='info-row'>
                  <label className='info-column'>
                    <span className='info-label'>Tire Disposal</span>
                    <TireDisposalToggle
                      boxClassName='input-field'
                      checked={editedJob.tire_disposal}
                      onChange={(checked) => {
                        setEditedJob(prev => ({ ...prev, tire_disposal: checked }));
                        setHasUnsavedChanges(true);
                      }}
                    />
                  </label>
                </div>
              </div>
              <div className='joborder-status'>
                <div className='info-row'>
                  <div className='info-column status-column'>
                    <div className='info-label'>Status</div>
                    <div className='status-control'>
                      <button 
                        type="button" 
                        className='status-arrow' 
                        onClick={() => handleStatusChange('prev')}
                      >
                        ←
                      </button>
                      <select 
                        name="status" 
                        value={editedJob.status} 
                        onChange={handleChange}
                        className='input-field status-select'
                      >
                        <option value="">Select Status</option>
                        {choices.status && choices.status
                          .sort((a, b) => a.order - b.order)
                          .map(choice => (
                            <option key={choice.value} value={choice.value}>
                              {choice.display_name}
                            </option>
                          ))}
                      </select>
                      <button 
                        type="button" 
                        className='status-arrow' 
                        onClick={() => handleStatusChange('next')}
                      >
                        →
                      </button>
                    </div>
                  </div>
                  <div className='info-column'>
                    <div className='info-label'>Date Created</div>
                    <input 
                      type="datetime-local" 
                      value={editedJob.date_created ? (() => {
                        const date = new Date(editedJob.date_created);
                        // Convert to Eastern Time
                        const easternDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }));
                        // Format for datetime-local input (YYYY-MM-DDTHH:MM)
                        const year = easternDate.getFullYear();
                        const month = String(easternDate.getMonth() + 1).padStart(2, '0');
                        const day = String(easternDate.getDate()).padStart(2, '0');
                        const hours = String(easternDate.getHours()).padStart(2, '0');
                        const minutes = String(easternDate.getMinutes()).padStart(2, '0');
                        return `${year}-${month}-${day}T${hours}:${minutes}`;
                      })() : ''} 
                      disabled 
                      className='input-field' 
                    />
                  </div>
                  <div className='info-column'>
                    <div className='info-label'>Date Promised</div>
                    <input 
                      type="date" 
                      name="datePromised" 
                      value={editedJob.date_promised || ''} 
                      onChange={handleChange} 
                      className='input-field' 
                    />
                  </div>
                  <div className='info-column'>
                    <div className='info-label'>Time Promised</div>
                    <input 
                      type="time" 
                      name="timePromised" 
                      value={editedJob.time_promised || ''} 
                      onChange={handleChange} 
                      className='input-field' 
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='joborder-bottom'>
            <label className='label-field'>
              Shop Notes:
              <textarea 
                name="shopNotes" 
                value={editedJob.shop_notes || ''} 
                onChange={handleChange} 
                className='input-field notes' 
              />
            </label>
          </div>
        </div>
        <div className='joborder-right'>
          <div className='customer-info'>
            <label className='label-field'>
              Customer Name:
              <div 
                className='customer-name-link'
                onClick={handleCustomerClick}
              >
                {editedJob.customer_name || editedJob.customer}
              </div>
            </label>
            <label className='label-field'>
              Repair Order Number (RO):
              <input type="text" name="ro" value={editedJob.ro} onChange={handleChange} className='input-field' />
            </label>
            <label className='label-field'>
              Purchase Order Number (PO):
              <input type="text" name="po" value={editedJob.po} onChange={handleChange} className='input-field' />
            </label>
            <label className='label-field'>
              Customer Notes:
              <textarea 
                name="customerNotes" 
                value={editedJob.customer_notes || ''} 
                onChange={handleChange} 
                className='input-field notes' 
              />
            </label>
            <label className='label-field'>
              Invoice Number:
              <span className='input-field invoice-number-display'>
                {editedJob.invoice_number ? (
                  editedJob.invoice_link && editedJob.invoice_link.trim() ? (
                    <a 
                      href={editedJob.invoice_link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="invoice-link"
                      title="Open invoice in QuickBooks"
                    >
                      {editedJob.invoice_number}
                    </a>
                  ) : (
                    <span title="Invoice link not available">
                      {editedJob.invoice_number}
                    </span>
                  )
                ) : (
                  <span className="no-invoice">No invoice yet</span>
                )}
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className='savebuttonbanner'>
        {/* Desktop buttons */}
        <div className="desktop-buttons">
          <button type='button' className='historybutton' onClick={handleHistoryClick}>History</button>
          {!fromWorkOrder && job.work_order && (
            <button type='button' className='historybutton' onClick={handleWorkOrderClick}>View Work Order</button>
          )}
          {!editedJob.invoice_number && (
            <button 
              type='button' 
              className='historybutton' 
              onClick={handleConvertToInvoice}
            >
              Convert to Invoice
            </button>
          )}
          <button type='button' className='historybutton' onClick={handleCopy}>Copy</button>
          {job.is_archived ? (
            <button type='button' className='unarchivebutton' onClick={handleUnarchive}>Unarchive</button>
          ) : (
            <button type='button' className='archivebutton' onClick={handleArchive}>Archive</button>
          )}
        </div>

        {/* Mobile buttons */}
        <div className="mobile-buttons">
          <div className="mobile-more-button-container">
            <button 
              type='button' 
              className='mobile-more-button' 
              onClick={handleMobileMoreClick}
            >
              More
            </button>
            <div className={`mobile-more-dropdown ${showMobileMoreDropdown ? 'show' : ''}`} style={{display: showMobileMoreDropdown ? 'block' : 'none'}}>
              <button
                type='button'
                className='historybutton'
                onClick={handleMobileCopy}
              >
                Copy
              </button>
              <button
                type='button'
                className='historybutton'
                onClick={handleMobileHistoryClick}
              >
                History
              </button>
              {!fromWorkOrder && job.work_order && (
                <button 
                  type='button' 
                  className='historybutton' 
                  onClick={handleMobileWorkOrderClick}
                >
                  View Work Order
                </button>
              )}
              {!editedJob.invoice_number && (
                <button 
                  type='button' 
                  className='historybutton' 
                  onClick={() => {
                    setShowMobileMoreDropdown(false);
                    handleConvertToInvoice();
                  }}
                >
                  Convert to Invoice
                </button>
              )}
              {job.is_archived ? (
                <button 
                  type='button' 
                  className='unarchivebutton' 
                  onClick={handleMobileUnarchive}
                >
                  Unarchive
                </button>
              ) : (
                <button 
                  type='button' 
                  className='archivebutton' 
                  onClick={handleMobileArchive}
                >
                  Archive
                </button>
              )}
            </div>
          </div>
          <button className='mobile-save-button' type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
        </div>

        <button className='savebutton' type="submit" disabled={isSaving}>{isSaving ? 'Saving...' : 'Save'}</button>
      </div>
      {showHistory && <JobHistory jobId={job.id} onClose={() => setShowHistory(false)} />}
      {showUnsavedWarning && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unsaved Changes</h3>
            <p>You have unsaved changes. Are you sure you want to leave?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmClose}>Leave</button>
              <button onClick={handleCancelClose}>Stay</button>
            </div>
          </div>
        </div>
      )}
      {showArchiveConfirmation && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Archive Job</h3>
            <p>Are you sure you want to archive this job?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmArchive}>Archive</button>
              <button onClick={handleCancelArchive}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showUnarchiveConfirmation && (
        <div className="unsaved-changes-modal">
          <div className="unsaved-changes-content">
            <h3>Unarchive Job</h3>
            <p>Are you sure you want to unarchive this job?</p>
            <div className="unsaved-changes-buttons">
              <button onClick={handleConfirmUnarchive}>Unarchive</button>
              <button onClick={handleCancelUnarchive}>Cancel</button>
            </div>
          </div>
        </div>
      )}
      {showFullscreenImage && getCurrentGalleryPhoto() && (
        <div className="fullscreen-image-viewer" onClick={handleCloseFullscreen}>
          <button 
            className={`fullscreen-close-button ${showFullscreenControls ? 'visible' : 'hidden'}`} 
            onClick={(e) => {
              e.stopPropagation();
              handleCloseFullscreen(e, true);
            }}
            type="button"
          >
            ×
          </button>
          <div className={`fullscreen-top-controls ${showFullscreenControls ? 'visible' : 'hidden'}`}>
            {currentGalleryIndex === 0 ? (
              <button onClick={handleChangePhotoFromFullscreen} type="button">Change Main Photo</button>
            ) : (
              <button 
                onClick={(e) => {
                  const photo = additionalPhotos[currentGalleryIndex - 1];
                  if (photo) {
                    handleDeletePhoto(photo.id, e);
                  }
                }}
                type="button"
              >
                Delete Photo
              </button>
            )}
            <button 
              className="fullscreen-add-photos-button"
              onClick={(e) => {
                e.stopPropagation();
                additionalPhotosInputRef.current.click();
              }}
              disabled={isUploadingPhotos}
              type="button"
            >
              {isUploadingPhotos ? 'Uploading...' : `+ Add Photos ${additionalPhotos.length > 0 ? `(${additionalPhotos.length})` : ''}`}
            </button>
            <span className="gallery-counter">
              {currentGalleryIndex + 1} / {1 + additionalPhotos.length}
            </span>
          </div>
          {/* Navigation arrows - only show if there are multiple photos */}
          {additionalPhotos.length > 0 && (
            <>
              <button 
                className={`gallery-nav-button gallery-prev ${showFullscreenControls ? 'visible' : 'hidden'}`}
                onClick={handleGalleryPrev}
                type="button"
              >
                ‹
              </button>
              <button 
                className={`gallery-nav-button gallery-next ${showFullscreenControls ? 'visible' : 'hidden'}`}
                onClick={handleGalleryNext}
                type="button"
              >
                ›
              </button>
            </>
          )}
          <div 
            className="fullscreen-image-container" 
            onClick={(e) => { 
              e.stopPropagation(); 
              handleToggleFullscreenControls(); 
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <img 
              src={getCurrentGalleryPhoto()}
              alt="Job Fullscreen"
              className="fullscreen-image"
              style={{ 
                transform: `translate(${imagePosition.x}px, ${imagePosition.y}px) scale(${imageScale})`,
                transition: isPinching.current ? 'none' : 'transform 0.2s ease-out'
              }}
              draggable={false}
            />
          </div>
        </div>
      )}
    </form>
  );
};

export default JobOrder;

